import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { BrandProvider } from './brand/useBrand';
import BrandSEO from './brand/BrandSEO';
import BrandLandingRouter from './brand/BrandLandingRouter';
import MarketplaceGate from './brand/MarketplaceGate';
import AuthCallback from './pages/AuthCallback';
import AppleAuthComplete from './pages/AppleAuthComplete';

// ── Eager imports — kept in the main bundle for the hottest routes.
// First paint on /, /property/:id, /wifi-access/:unitCode must stay
// snappy. Sentry flagged render-blocking payload on /wifi-access
// (issue 7458844302) — lazy-loading everything else below trims the
// initial JS the browser has to parse on those cold entrypoints.
import HomePage from "./pages/HomePage";
import PropertyDetailPage from "./pages/PropertyDetailPage";
import WiFiAccessPage from "./pages/WiFiAccessPage";
import WelcomePage from "./pages/WelcomePage";
import Header from "./components/Header";
import WonderCreditsPage from "./pages/WonderCreditsPage";
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import { captureRef } from "./lib/ref";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { ConsentBanner } from "./components/legal/ConsentBanner";
import { FEATURE_FLAGS } from "./config";
import { SessionBoundary } from './components/hosts/mod08/SessionBoundary';

// ── Lazy-loaded routes — each becomes its own webpack chunk so cold
// visitors to /wifi-access never download the host/admin dashboard JS.
const BookingSuccessPage = lazy(() => import("./pages/BookingSuccessPage"));
const CaptivePortalPage = lazy(() => import("./pages/CaptivePortalPage"));
const AdminLoginPage = lazy(() => import("./pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const CustomerSignupPage = lazy(() => import("./pages/CustomerSignupPage"));
const CustomerLoginPage = lazy(() => import("./pages/CustomerLoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const CustomerDashboardPage = lazy(() => import("./pages/BrandDashboardRouter"));
const BookingLookupPage = lazy(() => import("./pages/BookingLookupPage"));
const WishlistsPage = lazy(() => import("./pages/WishlistsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const LocationPropertiesPage = lazy(() => import("./pages/LocationPropertiesPage"));
const CreatorDashboardPage = lazy(() => import("./pages/CreatorDashboardPage"));
const ConciergePage = lazy(() => import("./pages/ConciergePage"));
const TripPlannerPage = lazy(() => import("./pages/TripPlannerPage"));
const CommunityProfilePage = lazy(() => import("./pages/CommunityProfilePage"));
const UCWonderAIIntroPage = lazy(() => import("./pages/UCWonderAIIntroPage"));
const HeroSimPreview = lazy(() => import("./pages/HeroSimPreview"));
const SketchHeroPreview = lazy(() => import("./pages/SketchHeroPreview"));
const SharedTripPage = lazy(() => import("./pages/SharedTripPage"));
const HostsWorkspacePage = lazy(() => import("./pages/hosts/HostsWorkspacePage"));
const Module04PreviewPage = lazy(() => import("./pages/hosts/Module04PreviewPage"));
const Module04SourcesPage = lazy(() => import("./pages/hosts/Module04SourcesPage"));
const Module04RealPricingPage = lazy(() => import("./pages/hosts/Module04RealPricingPage"));
const RevenueBookingsPage = lazy(() => import("./pages/hosts/RevenueBookingsPage"));
const RevenueSettingsPage = lazy(() => import("./pages/hosts/RevenueSettingsPage"));
const RevenueHistoryPage = lazy(() => import("./pages/hosts/RevenueHistoryPage"));
const RevenueRouteRedirect = lazy(() => import("./components/hosts/mod04/RevenueRouteRedirect").then(module=>({default:module.RevenueRouteRedirect})));
const RevenueWorkspace = lazy(() => import("./components/hosts/mod04/RevenueWorkspace").then(module => ({ default: module.RevenueWorkspace })));
const PolicyReviewPage = lazy(() => import("./pages/hosts/PolicyReviewPage"));
const WorkerPage = lazy(() => import("./pages/WorkerPage"));
const HostsSignupPage = lazy(() => import("./pages/hosts/HostsSignupPage"));
const HostsJoinPage = lazy(() => import("./pages/hosts/HostsJoinPage"));
const HostsQrPrintPage = lazy(() => import("./pages/hosts/HostsQrPrintPage"));
const ConciergeStayPage = lazy(() => import("./pages/hosts/ConciergeStayPage"));
const AfterStayPage = lazy(() => import("./pages/hosts/AfterStayPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const StudentsPage = lazy(() => import("./pages/StudentsPage"));
const MedicalPage = lazy(() => import("./pages/MedicalPage"));
const InsurancePage = lazy(() => import("./pages/InsurancePage"));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage"));
const AboutMontrealPage = lazy(() => import("./pages/AboutMontrealPage"));
const MontrealAttractionsPage = lazy(() => import("./pages/MontrealAttractionsPage"));
const ThingsToDoMontrealPage = lazy(() => import("./pages/ThingsToDoMontrealPage"));
const AirCoverPage = lazy(() => import("./pages/AirCoverPage"));
const SafetyInformationPage = lazy(() => import("./pages/SafetyInformationPage"));
const CancellationOptionsPage = lazy(() => import("./pages/CancellationOptionsPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const NewsroomPage = lazy(() => import("./pages/NewsroomPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const FoundersLetterPage = lazy(() => import("./pages/FoundersLetterPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const InvestorsPage = lazy(() => import("./pages/InvestorsPage"));
const MapTestPage = lazy(() => import("./pages/MapTestPage"));
const LogoPreviewPage = lazy(() => import("./pages/LogoPreviewPage"));
const ExperienceMontrealPage = lazy(() => import("./pages/ExperienceMontrealPage"));
const ExperienceMontrealComingSoonPage = lazy(() => import("./pages/ExperienceMontrealComingSoonPage"));
const GuestProfilePage = lazy(() => import("./pages/GuestProfilePage"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Role-based layouts — only needed when a logged-in user lands on their
// dashboard, so lazy.
const CustomerLayout = lazy(() => import("./customer/layouts/CustomerLayout"));
const AdminLayout = lazy(() => import("./admin/layouts/AdminLayout"));

// Design previews — internal-only mockups. Never visited by real users.
const PreviewIndex = lazy(() => import("./pages/design-previews/PreviewIndex"));
const WelcomeMobileOptionsPreview = lazy(() => import("./pages/WelcomeMobileOptionsPreview"));
const ColorPalettePreview = lazy(() => import("./pages/ColorPalettePreview"));
const TravelpayoutsPreview = lazy(() => import("./pages/TravelpayoutsPreview"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const DataSecurityProtocolPage = lazy(() => import("./pages/DataSecurityProtocolPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const IconExplorerPage = lazy(() => import("./pages/IconExplorerPage"));
const HotelDealsPage = lazy(() => import("./pages/HotelDealsPage"));
const FlightDealsPage = lazy(() => import("./pages/FlightDealsPage"));
const CompanionPage = lazy(() => import("./pages/CompanionPage"));
const TripPassportPage = lazy(() => import("./pages/TripPassportPage"));
const WelcomeFinalPreview = lazy(() => import("./pages/WelcomeFinalPreview"));
const PaletteExtensionsPreview = lazy(() => import("./pages/PaletteExtensionsPreview"));
const GroupPage = lazy(() => import("./pages/GroupPage"));
const BecomePartnerPage = lazy(() => import("./pages/BecomePartnerPage"));
const PartnerApplyPage = lazy(() => import("./pages/PartnerApplyPage"));
const ExperiencesPage = lazy(() => import("./pages/BrandExperiencesRouter"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ExperienceDetailPage = lazy(() => import("./pages/ExperienceDetailPage"));
const PartnerSetupPasswordPage = lazy(() => import("./pages/PartnerSetupPasswordPage"));
const PartnerClaimPage = lazy(() => import("./pages/PartnerClaimPage"));
const PartnerLoginPage = lazy(() => import("./pages/PartnerLoginPage"));
const BecomeExperiencePartnerPage = lazy(() => import("./pages/BecomeExperiencePartnerPage"));
const PartnerDashboardPage = lazy(() => import("./pages/PartnerDashboardPage"));
const DiningPage = lazy(() => import("./pages/DiningPage"));
const DiningCheckoutSuccessPage = lazy(() => import("./pages/DiningCheckoutSuccessPage"));
const WalletPage = lazy(() => import("./pages/WalletPage"));
const PreviewA_Editorial = lazy(() => import("./pages/design-previews/PreviewA_Editorial"));
const PreviewB_RealEstateTech = lazy(() => import("./pages/design-previews/PreviewB_RealEstateTech"));
const PreviewC_Earthy = lazy(() => import("./pages/design-previews/PreviewC_Earthy"));
const PreviewD_SwissMinimal = lazy(() => import("./pages/design-previews/PreviewD_SwissMinimal"));
const PreviewE_GlassBento = lazy(() => import("./pages/design-previews/PreviewE_GlassBento"));
const PreviewF_CoastalBlue = lazy(() => import("./pages/design-previews/PreviewF_CoastalBlue"));
const PreviewG_ElectricBlue = lazy(() => import("./pages/design-previews/PreviewG_ElectricBlue"));
const PreviewH_SoftSage = lazy(() => import("./pages/design-previews/PreviewH_SoftSage"));
const PreviewI_PalePeach = lazy(() => import("./pages/design-previews/PreviewI_PalePeach"));
const PreviewJ_PowderBlue = lazy(() => import("./pages/design-previews/PreviewJ_PowderBlue"));
const PreviewK_Linen = lazy(() => import("./pages/design-previews/PreviewK_Linen"));
const PreviewL_LightBlueCurrent = lazy(() => import("./pages/design-previews/PreviewL_LightBlueCurrent"));

// Brand-aware landing — selects Stays vs Marketplace based on hostname (Phase 3.0)
// Public marketplace gate (Coming Soon) — flip to LandingMarketplace on launch day.
const LandingMarketplace = lazy(() => import("./pages/LandingMarketplace"));
const LandingMarketplaceComingSoon = lazy(() => import("./pages/LandingMarketplaceComingSoon"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const GuestWelcomePage = lazy(() => import("./pages/GuestWelcomePage"));
const WelcomeQRCardPage = lazy(() => import("./pages/WelcomeQRCardPage"));
const CommunityArticlePage = lazy(() => import("./pages/CommunityArticlePage"));
const UCWonderAdminLoginPage = lazy(() => import("./pages/UCWonderAdminLoginPage"));
const UCWonderAdminDashboardPage = lazy(() => import("./pages/UCWonderAdminDashboardPage"));
const AboutUCWonderPage = lazy(() => import("./pages/AboutUCWonderPage"));
const UCPassportPage = lazy(() => import("./pages/UCPassportPage"));

function App() {
  // Creator referral attribution — capture ?ref= before anything else.
  captureRef();
  // Emergent Google OAuth return — process session_id BEFORE any routing.
  if (window.location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <div className="App flex flex-col min-h-screen">
      <BrowserRouter>
        <ConsentBanner />
        <BrandProvider>
        <BrandSEO />
        <ScrollToTop />
        <AnalyticsTracker />

        <Suspense
          fallback={
            <div
              className="flex items-center justify-center min-h-screen bg-white"
              data-testid="route-suspense-fallback"
              aria-label="Loading page"
            >
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }
        >
        <Routes>
          {/* /marketplace-preview is OUTSIDE the gate — it's the
              private surface where the full marketplace landing is
              reviewable during the pre-launch period. */}
          <Route path="/marketplace-preview" element={<LandingMarketplace />} />

          {/* MarketplaceGate is now a passthrough (see MarketplaceGate.jsx —
              gate disabled 2026-07-03). Kept as the wrapper Route so we
              can re-enable a lockout window with a one-line edit if
              needed for maintenance / re-launch. */}
          <Route element={<MarketplaceGate />}>
          {/* B2B pivot: the root now renders the Hosts workspace (marketing
              funnel for anonymous visitors, live dashboard once logged in).
              The old traveler landing moved to /travel — still reachable via
              QR flows and direct URL, just no longer the public front door. */}
          <Route path="/" element={<HostsWorkspacePage />} />
          <Route path="/travel" element={
            <BrandLandingRouter
              stays={<WelcomePage />}
              marketplace={<LandingMarketplace />}
            />
          } />

          {/* AI Travel Concierge (M1 preview — link-only, no public CTA yet) */}
          <Route path="/concierge" element={<ConciergePage />} />
          <Route path="/plan" element={<TripPlannerPage />} />
          <Route path="/ucwonder-ai" element={<UCWonderAIIntroPage />} />
          <Route path="/hero-sim-preview" element={<HeroSimPreview />} />
          <Route path="/sketch-hero-preview" element={<SketchHeroPreview />} />
          <Route path="/trip/:token" element={<SharedTripPage />} />
          <Route path="/creator/:token" element={<CreatorDashboardPage />} />

          {/* Notifications inbox */}
          <Route path="/notifications" element={<NotificationsPage />} />

          {/* Design exploration — read-only previews (no Header/Footer) */}
          <Route path="/design-previews" element={<PreviewIndex />} />
          <Route path="/preview/welcome-mobile-options" element={<WelcomeMobileOptionsPreview />} />
          <Route path="/preview/color-palettes" element={<ColorPalettePreview />} />
          <Route path="/preview/icons" element={<IconExplorerPage />} />
          <Route path="/preview/welcome-final" element={<WelcomeFinalPreview />} />
          <Route path="/preview/palette-extensions" element={<PaletteExtensionsPreview />} />
          <Route path="/preview/travelpayouts" element={<TravelpayoutsPreview />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/data-security" element={<DataSecurityProtocolPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/group/:slug" element={<GroupPage />} />
          <Route path="/become-a-partner" element={<BecomePartnerPage />} />
          <Route path="/partner/apply" element={<PartnerApplyPage />} />
          <Route path="/experiences" element={<ExperiencesPage />} />
          <Route path="/hotels" element={<HotelDealsPage />} />
          <Route path="/flights" element={<FlightDealsPage />} />
          <Route path="/companion" element={<CompanionPage />} />
          {/* UCWonder for Hosts — B2B multi-tenant workspace + public QR concierge */}
          <Route path="/hosts" element={<HostsWorkspacePage />} />
          <Route path="/hosts/signup" element={<HostsSignupPage />} />
          <Route path="/hosts/join/:token" element={<HostsJoinPage />} />
          <Route path="/hosts/qr-print" element={<HostsQrPrintPage />} />
          <Route path="/hosts/revenue-preview" element={<SessionBoundary><RevenueWorkspace /></SessionBoundary>}>
            <Route index element={<Navigate replace to="/hosts/revenue-preview/recommendations" />} />
            {process.env.REACT_APP_MOD04_SYNTHETIC_SOURCES_ENABLED === 'true' && <Route path="sources" element={<Module04SourcesPage />} />}
            <Route path="recommendations" element={<Module04RealPricingPage />} />
            <Route path="bookings" element={<RevenueBookingsPage />} />
            <Route path="settings" element={<RevenueSettingsPage />} />
            <Route path="history" element={<RevenueHistoryPage />} />
            <Route path="testing" element={<Navigate replace to="/hosts/revenue-preview/testing/comparison" />} />
            {process.env.REACT_APP_MOD04_SYNTHETIC_SOURCES_ENABLED === 'true' && <Route path="testing/sources" element={<Module04SourcesPage />} />}
            <Route path="testing/*" element={<Module04PreviewPage />} />
            <Route path="*" element={<RevenueRouteRedirect />} />
          </Route>
          <Route path="/hosts/:module" element={<HostsWorkspacePage />} />
          <Route path="/hosts/benefits/:benefitView" element={<SessionBoundary><HostsWorkspacePage /></SessionBoundary>} />
          <Route path="/hosts/policy-review/*" element={<SessionBoundary><PolicyReviewPage /></SessionBoundary>} />
              <Route path="/hosts/financials/:financialView" element={<HostsWorkspacePage />} />
              <Route path="/hosts/operations/*" element={<SessionBoundary><HostsWorkspacePage /></SessionBoundary>} />
              <Route path="/hosts/compliance/*" element={<SessionBoundary><HostsWorkspacePage /></SessionBoundary>} />
          <Route path="/worker/*" element={<SessionBoundary><WorkerPage /></SessionBoundary>} />
          <Route path="/stay/:slug" element={<ConciergeStayPage />} />
          <Route path="/after-stay" element={<AfterStayPage />} />
          <Route path="/passport/:sessionId" element={<TripPassportPage />} />
          <Route path="/wonders" element={<><Header /><WonderCreditsPage /></>} />
          <Route path="/wonder-credits" element={<Navigate to="/wonders" replace />} />
          <Route path="/experiences/:slug" element={<ExperienceDetailPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/welcome" element={<GuestWelcomePage />} />
          <Route path="/welcome/:slug" element={<GuestWelcomePage />} />
          <Route path="/welcome-qr" element={<WelcomeQRCardPage />} />
          <Route path="/welcome/:slug/qr" element={<WelcomeQRCardPage />} />
          <Route path="/u/:username" element={<CommunityProfilePage />} />
          <Route path="/community/news/:slug" element={<CommunityArticlePage />} />
          <Route path="/about" element={<AboutUCWonderPage />} />
          <Route path="/passport" element={<UCPassportPage />} />
          {/* Rewards preview — public catalog of partner brands that
              can be triggered as guest rewards. Route renamed from
              `/dining` → `/rewards-preview` (2026-07-03) to align with
              the automated-guest-rewards positioning (we don't sell
              gift cards, we preview which partner rewards a booking
              can trigger). Legacy `/dining` kept as a passthrough so
              old marketing links, bookmarks, and partner redirects
              continue to resolve. */}
          <Route path="/rewards-preview" element={<DiningPage />} />
          <Route path="/dining" element={<DiningPage />} />
          <Route path="/dining/success" element={<DiningCheckoutSuccessPage />} />
          <Route path="/wallet" element={<WalletPage />} />
          <Route path="/partner/setup-password" element={<PartnerSetupPasswordPage />} />
          <Route path="/partner/claim/:token" element={<PartnerClaimPage />} />
          <Route path="/partner/login" element={<PartnerLoginPage />} />
          <Route path="/become-experience-partner" element={<BecomeExperiencePartnerPage />} />
          <Route path="/partner/dashboard" element={<PartnerDashboardPage />} />
          <Route path="/design-previews/editorial" element={<PreviewA_Editorial />} />
          <Route path="/design-previews/realestate-tech" element={<PreviewB_RealEstateTech />} />
          <Route path="/design-previews/earthy" element={<PreviewC_Earthy />} />
          <Route path="/design-previews/swiss-minimal" element={<PreviewD_SwissMinimal />} />
          <Route path="/design-previews/glass-bento" element={<PreviewE_GlassBento />} />
          <Route path="/design-previews/coastal-blue" element={<PreviewF_CoastalBlue />} />
          <Route path="/design-previews/electric-blue" element={<PreviewG_ElectricBlue />} />
          <Route path="/design-previews/soft-sage" element={<PreviewH_SoftSage />} />
          <Route path="/design-previews/pale-peach" element={<PreviewI_PalePeach />} />
          <Route path="/design-previews/powder-blue" element={<PreviewJ_PowderBlue />} />
          <Route path="/design-previews/linen" element={<PreviewK_Linen />} />
          <Route path="/design-previews/light-blue-current" element={<PreviewL_LightBlueCurrent />} />

          {/* Public routes with Header/Footer */}
          <Route path="/search" element={<><Header /><HomePage /><Footer /></>} />
          <Route path="/property/:id" element={<><Header /><PropertyDetailPage /><Footer /></>} />
          <Route path="/location/:location" element={<><Header /><LocationPropertiesPage /><Footer /></>} />
          
          {/* Target Audience Pages */}
          <Route path="/students" element={<><Header /><StudentsPage /><Footer /></>} />
          <Route path="/medical" element={<><Header /><MedicalPage /><Footer /></>} />
          <Route path="/insurance" element={<><Header /><InsurancePage /><Footer /></>} />

          {/* Info Pages */}
          <Route path="/help-center" element={<><Header /><HelpCenterPage /><Footer /></>} />
          <Route path="/about-montreal" element={<><Header /><AboutMontrealPage /><Footer /></>} />
          <Route path="/montreal-attractions" element={<><Header /><MontrealAttractionsPage /><Footer /></>} />
          <Route path="/things-to-do-montreal" element={<><Header /><ThingsToDoMontrealPage /><Footer /></>} />
          <Route path="/aircover" element={<><Header /><AirCoverPage /><Footer /></>} />
          <Route path="/safety-information" element={<><Header /><SafetyInformationPage /><Footer /></>} />
          <Route path="/cancellation-options" element={<><Header /><CancellationOptionsPage /><Footer /></>} />
          <Route path="/sitemap" element={<><Header /><SitemapPage /><Footer /></>} />
          <Route path="/newsroom" element={<><Header /><NewsroomPage /><Footer /></>} />
          <Route path="/features" element={<><Header /><FeaturesPage /><Footer /></>} />
          <Route path="/founders-letter" element={<><Header /><FoundersLetterPage /><Footer /></>} />
          <Route path="/careers" element={<><Header /><CareersPage /><Footer /></>} />
          <Route path="/investors" element={<><Header /><InvestorsPage /><Footer /></>} />
          <Route path="/map-test" element={<><Header /><MapTestPage /><Footer /></>} />
          <Route path="/logo-preview" element={<LogoPreviewPage />} />
          <Route
            path="/experience-montreal"
            element={
              <>
                <Header />
                {FEATURE_FLAGS.experienceMontreal
                  ? <ExperienceMontrealPage />
                  : <ExperienceMontrealComingSoonPage />}
                <Footer />
              </>
            }
          />

          {/* Legal Pages */}
          <Route path="/terms" element={<><Header /><TermsOfService /><Footer /></>} />
          <Route path="/privacy" element={<><Header /><PrivacyPolicy /><Footer /></>} />

          {/* Customer-specific pages (with CustomerLayout) */}
          <Route path="/booking/success" element={
            <CustomerLayout>
              <BookingSuccessPage />
            </CustomerLayout>
          } />
          
          {/* Customer routes (with CustomerLayout for role-based context) */}
          <Route path="/signup" element={<><Header /><CustomerSignupPage /><Footer /></>} />
          <Route path="/login" element={<><Header /><CustomerLoginPage /><Footer /></>} />
          <Route path="/customer-login" element={<><Header /><CustomerLoginPage /><Footer /></>} />
          <Route path="/forgot-password" element={<><Header /><ForgotPasswordPage /><Footer /></>} />
          <Route path="/verify-email" element={<><Header /><VerifyEmailPage /><Footer /></>} />
          <Route path="/auth/apple/complete" element={<AppleAuthComplete />} />
          <Route path="/dashboard" element={
            <ErrorBoundary>
              <CustomerLayout>
                <CustomerDashboardPage />
              </CustomerLayout>
            </ErrorBoundary>
          } />
          <Route path="/booking-lookup" element={
            <CustomerLayout>
              <BookingLookupPage />
            </CustomerLayout>
          } />
          <Route path="/wishlists" element={
            <CustomerLayout>
              <WishlistsPage />
            </CustomerLayout>
          } />
          <Route path="/messages" element={
            <CustomerLayout>
              <MessagesPage />
            </CustomerLayout>
          } />
          
          {/* Captive Portal (no header/footer) */}
          <Route path="/captive-portal" element={<CaptivePortalPage />} />
          
          {/* WiFi Access (no header/footer - clean experience) */}
          <Route path="/wifi-access/:unitCode" element={<WiFiAccessPage />} />
          
          {/* Admin routes (SECURE - obscured URL path) */}
          <Route path="/sys-9k4x7m2n8p5w3q1r6t/login" element={<AdminLoginPage />} />
          <Route path="/ucw-sys-7h3k9w2m5x/login" element={<UCWonderAdminLoginPage />} />
          <Route path="/ucw-sys-7h3k9w2m5x/dashboard" element={<UCWonderAdminDashboardPage />} />
          <Route path="/sys-9k4x7m2n8p5w3q1r6t/dashboard" element={
            <AdminLayout>
              <AdminDashboardPage />
            </AdminLayout>
          } />
          
          {/* Guest Profile */}
          <Route path="/guest/:guestId" element={<><Header /><GuestProfilePage /><Footer /></>} />
        </Route> {/* end <MarketplaceGate> layout route */}

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </BrandProvider>
      </BrowserRouter>
      <Toaster />
      <SonnerToaster position="top-right" richColors closeButton expand visibleToasts={6} />
    </div>
  );
}

export default App;
